import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { IPublicPricing, PublicMaterialKey, usePublicPricing } from '../../services/pricing_public/pricing_public';
import { ArtistPathPanel } from './ArtistPathPanel.component';
import { Segmented, Slider } from './Controls.component';
import { EarningsCalculator } from './EarningsCalculator.component';
import { estimateStoreOrder, estimateWall, money, retailSliderRange, WALL_PRESETS } from './earnings';
import { PathPicker, PricingPath } from './PathPicker.component';
import { StepItem } from './StepList.component';
import { BRAND_STEPS, StoreOwnerUnlock } from './StoreOwnerUnlock.component';
import { StoreProfitCalculator } from './StoreProfitCalculator.component';
import { ZeroCostStrip } from './ZeroCostStrip.component';

export type { PricingPath } from './PathPicker.component';

// #region ARTIST_STEPS
/** The Artist path, in order. Callers add routes and done flags where they know them. */
export const ARTIST_STEPS: StepItem[] = [
    { label: 'Upload a design' },
    { label: 'Set a licensing price on it' },
    { label: 'Set up payouts with Stripe' },
    { label: 'Brands license it, and you are paid monthly' }
];

export interface ArtistPricingWidgetProps {
    /** 'dashboard' sits in the narrower app column; 'public' fills the embed frame. */
    variant: 'dashboard' | 'public';
    initialPath?: PricingPath;
    onDismiss?: () => void;
    brandSteps?: StepItem[];
    artistSteps?: StepItem[];
    /** The brand already has Shopify connected. */
    unlocked?: boolean;
    brandAction?: React.ReactNode;
    /** The strip under the widget, for the path being shown. */
    footer?: (path: PricingPath) => React.ReactNode;
}

const SHORT_LABEL: Record<string, string> = { traditional: 'Traditional', peel: 'Peel & Stick', peel_luxe: 'Luxe Peel' };

// Static class strings so Tailwind keeps them. The dashboard column is narrower, so it splits later.
const LAYOUT = {
    dashboard: {
        split: 'grid gap-8 px-5 pb-6 pt-6 sm:px-8 2xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] 2xl:gap-10',
        divider: 'border-t border-surface-border pt-8 2xl:border-l 2xl:border-t-0 2xl:pl-10 2xl:pt-0',
        calculator: 'lg:grid-cols-2',
        panel: 'lg:grid-cols-2 2xl:grid-cols-1'
    },
    public: {
        split: 'grid gap-8 px-5 pb-6 pt-6 sm:px-8 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] xl:gap-10',
        divider: 'border-t border-surface-border pt-8 xl:border-l xl:border-t-0 xl:pl-10 xl:pt-0',
        calculator: 'sm:grid-cols-2',
        panel: 'md:grid-cols-2 xl:grid-cols-1'
    }
};

// #region ArtistPricingWidget
/**
 * Wallmates pricing, as two separate paths. Brand: sell in your own store on
 * Store Owner pricing. Artist: license designs to other brands for a royalty.
 * Every rate comes from the public pricing endpoint.
 */
export const ArtistPricingWidget: React.FunctionComponent<ArtistPricingWidgetProps> = (props): React.ReactElement | null => {
    const pricing = usePublicPricing();
    const [path, setPath] = React.useState<PricingPath>(props.initialPath ?? 'brand');
    if (pricing === null && props.variant === 'dashboard') return null;
    return (
        <section aria-labelledby="artist-pricing-title" className="relative overflow-hidden rounded-2xl border border-surface-border bg-surface-raised text-zinc-900 shadow-sm dark:text-zinc-100">
            <div className="px-5 pt-6 sm:px-8 sm:pt-8">
                {props.onDismiss ? (
                    <button type="button" onClick={props.onDismiss} aria-label="Hide pricing" className="absolute right-3 top-3 rounded-full p-1.5 text-zinc-400 hover:bg-surface-muted hover:text-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:hover:text-zinc-200">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                ) : null}
                <p className="text-xs font-semibold uppercase tracking-wider text-brand">Pricing</p>
                <h2 id="artist-pricing-title" className="mt-1 max-w-2xl pr-8 font-display text-3xl font-semibold tracking-tight text-black dark:text-white sm:text-4xl">Two ways to earn with Wallmates</h2>
                <p className="mt-2 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400 sm:text-base">Pick the one that fits you. You can do both.</p>
                <div className="mt-5"><PathPicker value={path} onChange={setPath} /></div>
            </div>
            {pricing === undefined ? <Skeleton /> : null}
            {pricing === null ? <p className="px-5 py-6 text-sm text-zinc-500 sm:px-8">Pricing is unavailable right now. Please try again shortly.</p> : null}
            {pricing ? <Body pricing={pricing} path={path} {...props} /> : null}
        </section>
    );
};

const Body: React.FunctionComponent<ArtistPricingWidgetProps & { pricing: IPublicPricing; path: PricingPath }> = ({ pricing, path, variant, unlocked, brandAction, brandSteps, artistSteps, footer }): React.ReactElement => {
    const [wallId, setWallId] = React.useState(WALL_PRESETS[0].id);
    const [materialKey, setMaterialKey] = React.useState<PublicMaterialKey>('traditional');
    const [ordersPerMonth, setOrdersPerMonth] = React.useState(5);
    const [royalty, setRoyalty] = React.useState(10);
    const [retail, setRetail] = React.useState<number | null>(null);

    const layout = LAYOUT[variant];
    const wall = WALL_PRESETS.find((w) => w.id === wallId) ?? WALL_PRESETS[0];
    const material = pricing.materials.find((m) => m.key === materialKey) ?? pricing.materials[0];
    const range = pricing.retail_ranges.find((r) => r.key === material.key);
    const bounds = retailSliderRange(material, range);
    const retailSqft = retail ?? bounds.start;
    const brand = path === 'brand';

    const sharedControls = (
        <>
            <Segmented
                label="Wall"
                value={wallId}
                onChange={setWallId}
                options={WALL_PRESETS.map((w) => ({ value: w.id, title: w.label.replace(' wall', ''), hint: `${w.widthFt} × ${w.heightFt} ft` }))}
            />
            <Segmented
                label="Material"
                value={materialKey}
                // A new material starts at its own typical retail price.
                onChange={(key) => { setMaterialKey(key); setRetail(null); }}
                options={pricing.materials.map((m) => ({ value: m.key, title: SHORT_LABEL[m.key] ?? m.label, hint: brand ? `cost ${money(m.store_owner_cost_sqft)}` : `${money(m.royalty_basis_sqft)}/sq ft` }))}
            />
            <Slider id="pricing-orders" label="Orders a month" display={String(ordersPerMonth)} value={ordersPerMonth} min={1} max={500} onChange={setOrdersPerMonth} />
        </>
    );

    return (
        <>
            {brand ? <div className="px-5 pt-5 sm:px-8"><ZeroCostStrip prepaidTopUp={pricing.store_owner_fees.prepaid_top_up} /></div> : null}
            <div className={layout.split}>
                <div>
                    <h3 className="mb-5 font-display text-xl font-semibold tracking-tight text-black dark:text-white">
                        {brand ? 'What you could keep as a Brand' : 'What you could earn as an Artist'}
                    </h3>
                    {brand ? (
                        <StoreProfitCalculator
                            pricing={pricing} material={material} range={range} bounds={bounds}
                            retail={retailSqft} onRetail={setRetail}
                            estimate={estimateStoreOrder(wall, material, retailSqft, pricing)}
                            ordersPerMonth={ordersPerMonth} sharedControls={sharedControls} columns={layout.calculator}
                        />
                    ) : (
                        <EarningsCalculator
                            pricing={pricing} material={material} royalty={royalty} onRoyalty={setRoyalty}
                            estimate={estimateWall(wall, material, royalty, pricing)}
                            ordersPerMonth={ordersPerMonth} sharedControls={sharedControls} columns={layout.calculator}
                        />
                    )}
                    <FinePrint path={path} pricing={pricing} />
                </div>
                <div className={layout.divider}>
                    {brand
                        ? <StoreOwnerUnlock pricing={pricing} steps={brandSteps ?? BRAND_STEPS} wall={wall} material={material} estimate={estimateWall(wall, material, royalty, pricing)} ordersPerMonth={ordersPerMonth} unlocked={unlocked} action={brandAction} columns={layout.panel} />
                        : <ArtistPathPanel pricing={pricing} steps={artistSteps ?? ARTIST_STEPS} columns={layout.panel} />}
                </div>
            </div>
            {footer ? <div className="px-5 pb-5 sm:px-8 sm:pb-8">{footer(path)}</div> : null}
        </>
    );
};

// #region FinePrint
const FinePrint: React.FunctionComponent<{ path: PricingPath; pricing: IPublicPricing }> = ({ path, pricing }): React.ReactElement => {
    const exempt = pricing.royalty_exempt_panel_heights_ft.join(', ').replace(/, (\d+)$/, ' or $1');
    return (
        <p className="mt-5 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
            {path === 'brand'
                ? <>An example, not a promise of sales. The price range is what stores on Wallmates charge today. Selling your own designs means no royalty to pay. Shipping and boxes are extra.</>
                : <>An example, not a promise of sales. Your royalty is worked out on Wallmates&rsquo; standard value of the wall, so a brand&rsquo;s markup never changes your cut. Samples and single {exempt} ft panels are royalty-free.</>}
        </p>
    );
};

const Skeleton: React.FunctionComponent = (): React.ReactElement => (
    <div className="grid animate-pulse gap-6 px-5 py-6 sm:px-8 md:grid-cols-2" aria-busy="true" aria-label="Loading pricing">
        <div className="space-y-5">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-12 rounded-xl bg-surface-muted" />)}
        </div>
        <div className="h-64 rounded-2xl bg-surface-muted" />
    </div>
);
